// Diálogo para compartir la convocatoria por WhatsApp: elige la antelación con
// la que deben llegar los jugadores y genera el mensaje listo para copiar/enviar.
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SendIcon from "@mui/icons-material/Send";

import ModalShell from "./ModalShell";
import MensajeWhatsApp from "./MensajeWhatsApp";
import { useNotify } from "./NotificationProvider";
import { construirMensajeConvocatoria } from "utils/convocatoria";

const ANTELACIONES = [15, 30, 45, 60, 90];

export default function ShareConvocatoriaDialog({
  open,
  onClose,
  partido,
  equipoNombre,
  convocados,
}) {
  const notify = useNotify();
  const [antelacion, setAntelacion] = useState(45);

  const mensaje = useMemo(
    () =>
      construirMensajeConvocatoria({
        partido,
        equipoNombre,
        convocados,
        antelacionMin: antelacion,
      }),
    [partido, equipoNombre, convocados, antelacion]
  );

  const handleCopiar = async () => {
    try {
      await navigator.clipboard.writeText(mensaje);
      notify.success("Mensaje copiado. Pégalo en el grupo de WhatsApp del equipo.");
    } catch (err) {
      notify.error("No se pudo copiar. Selecciona el texto manualmente.");
    }
  };

  const handleAbrirWhatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(mensaje)}`, "_blank", "noopener");
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Compartir convocatoria"
      subtitle={partido ? `vs ${partido.equipo_rival}` : ""}
      color="success"
      maxWidth="sm"
      actions={
        <>
          <Button onClick={onClose} sx={{ color: "text.secondary" }}>
            Cerrar
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopiar}
            sx={{
              borderRadius: 2,
              borderColor: "grey.400",
              color: "text.primary",
              "&:hover": { borderColor: "success.main", color: "success.main" },
            }}
          >
            Copiar mensaje
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<SendIcon />}
            onClick={handleAbrirWhatsapp}
            sx={{ borderRadius: 2 }}
          >
            Abrir WhatsApp
          </Button>
        </>
      }
    >
      <Stack spacing={2.5}>
        <TextField
          select
          label="Convocar a los jugadores con esta antelación"
          value={antelacion}
          onChange={(e) => setAntelacion(Number(e.target.value))}
          helperText={
            partido?.hora
              ? "Se calculará la hora de convocatoria a partir de la hora del partido."
              : "El partido no tiene hora asignada todavía."
          }
          fullWidth
        >
          {ANTELACIONES.map((min) => (
            <MenuItem key={min} value={min}>
              {min} minutos antes
            </MenuItem>
          ))}
        </TextField>

        <MensajeWhatsApp texto={mensaje} sx={{ maxHeight: 360, overflowY: "auto" }} />
      </Stack>
    </ModalShell>
  );
}

ShareConvocatoriaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  partido: PropTypes.shape({
    equipo_rival: PropTypes.string,
    fecha: PropTypes.string,
    hora: PropTypes.string,
    jornada: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    local: PropTypes.bool,
    lugar: PropTypes.string,
  }),
  equipoNombre: PropTypes.string,
  convocados: PropTypes.arrayOf(
    PropTypes.shape({
      nombre: PropTypes.string,
      numero: PropTypes.number,
    })
  ),
};

ShareConvocatoriaDialog.defaultProps = {
  partido: null,
  equipoNombre: "",
  convocados: [],
};
